import React from 'react';
import { motion } from 'framer-motion';
import { 
  Stethoscope, 
  Pill, 
  Building2, 
  MapPin, 
  Bed, 
  UserCheck, 
  ClipboardList,
  LucideIcon
} from 'lucide-react';
import { ServiceItem } from '../types';
import { useLanguageStore } from '../store/useLanguageStore';

interface ServiceBoxProps extends ServiceItem {
  index: number;
}

const iconMap: Record<string, LucideIcon> = {
  stethoscope: Stethoscope,
  pill: Pill,
  building: Building2,
  mappin: MapPin,
  bed: Bed,
  usercheck: UserCheck,
  clipboard: ClipboardList,
};

const ServiceBox: React.FC<ServiceBoxProps> = ({
  id,
  title,
  titleSinhala,
  icon,
  bgImage,
  description,
  descriptionSinhala,
  stats,
  onClick,
  index
}) => {
  const { t, currentLanguage } = useLanguageStore();
  const IconComponent = iconMap[icon] || Stethoscope;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'bg-success-green';
      case 'inactive': return 'bg-error-red';
      case 'maintenance': return 'bg-warning-amber';
      default: return 'bg-primary-green';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'active': return t('status-active', 'Active', 'ක්‍රියාකාරී');
      case 'inactive': return t('status-inactive', 'Inactive', 'අක්‍රිය');
      case 'maintenance': return t('status-maintenance', 'Maintenance', 'නඩත්තු');
      default: return t('status-available', 'Available', 'ලබා ගත හැකි');
    }
  };

  // Extract color from bgImage for the left border
  const getBorderColor = (bgImage: string): string => {
    if (bgImage.includes('#16a34a')) return '#16a34a'; // Green
    if (bgImage.includes('#dc2626')) return '#dc2626'; // Red
    if (bgImage.includes('#2563eb')) return '#2563eb'; // Blue
    if (bgImage.includes('#eab308')) return '#eab308'; // Yellow
    if (bgImage.includes('#9333ea')) return '#9333ea'; // Purple
    if (bgImage.includes('#78716c')) return '#78716c'; // Brown/Stone
    if (bgImage.includes('#6b7280')) return '#6b7280'; // Gray
    if (bgImage.includes('#84cc16')) return '#84cc16'; // Lime
    if (bgImage.includes('#ec4899')) return '#ec4899'; // Pink
    if (bgImage.includes('#8b5cf6')) return '#8b5cf6'; // Violet
    if (bgImage.includes('#f97316')) return '#f97316'; // Orange
    return '#16a34a'; // Default green
  };

  // Convert hex to rgba for proper animation
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const borderColor = getBorderColor(bgImage);

  return (
    <motion.div
      className="service-box h-80 group cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      whileHover={{ y: -8 }}
      onClick={onClick}
    >
      {/* White card with colored left border */}
      <div 
        className="absolute inset-0 bg-white rounded-xl shadow-sm group-hover:shadow-xl transition-all duration-300"
        style={{
          borderLeft: `6px solid ${borderColor}`,
          boxShadow: '0 2px 5px rgb(0 0 0 / 0.05), 0 10px 20px rgb(0 0 0 / 0.03)'
        }}
      />

      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-300 rounded-xl overflow-hidden">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id={`pattern-${id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="2" fill={borderColor} opacity="0.5"/>
              <path d="M10,5 L15,10 L10,15 L5,10 Z" fill={borderColor} opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#pattern-${id})`}/>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 p-6 h-full flex flex-col justify-between">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between mb-4">
            <motion.div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: hexToRgba(borderColor, 0.08),
                border: `2px solid ${hexToRgba(borderColor, 0.19)}`
              }}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <IconComponent className="w-7 h-7" style={{ color: borderColor }} />
            </motion.div>
            
            {stats?.status && (
              <motion.div
                className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(stats.status)}`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                {getStatusText(stats.status)}
              </motion.div>
            )}
          </div>

          <motion.h3
            className="text-xl font-bold mb-3 text-gray-900 group-hover:text-gray-700 transition-colors duration-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {currentLanguage.code === 'si' ? titleSinhala : title}
          </motion.h3>

          <motion.p
            className="text-gray-600 text-sm leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {currentLanguage.code === 'si' ? descriptionSinhala : description}
          </motion.p>
        </div>

        {/* Footer */}
        <div className="space-y-3">
          {/* Stats */}
          {stats && (
            <div className="space-y-2">
              {stats.count !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {t('count', 'Count', 'ගණන')}:
                  </span>
                  <span className="font-semibold text-gray-700">{stats.count}</span>
                </div>
              )}
              {stats.updated && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {t('updated', 'Updated', 'යාවත්කාලීන')}:
                  </span>
                  <span className="font-semibold text-gray-700">
                    {new Date(stats.updated).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Action Button */}
          <motion.button
            className="w-full border-2 rounded-lg py-2.5 px-4 text-sm font-semibold transition-all duration-300 group-hover:shadow-md"
            style={{
              borderColor: hexToRgba(borderColor, 0.25),
              color: borderColor,
              backgroundColor: 'rgba(255, 255, 255, 1)'
            }}
            whileHover={{ 
              scale: 1.02,
              backgroundColor: hexToRgba(borderColor, 0.06)
            }}
            whileTap={{ scale: 0.98 }}
          >
            {t('view-details', 'View Details', 'විස්තර බලන්න')}
          </motion.button>
        </div>
      </div>

      {/* Hover colored overlay */}
      <div 
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none"
        style={{ backgroundColor: borderColor }}
      />
      
      {/* Click Ripple Effect */}
      <motion.div
        className="absolute inset-0 rounded-xl opacity-0"
        style={{ backgroundColor: borderColor }}
        whileTap={{ opacity: [0, 0.1, 0] }}
        transition={{ duration: 0.4 }}
      />
    </motion.div>
  );
};

export default ServiceBox;
